import Dexie from 'dexie'
import { VoyagePlan } from '../src/models/vessel-planning-model'
import { ScheduleEntry, VesselStatusSchedule, VesselStatusActual, VesselCharacteristicsIdb } from '../src/models/types/voyage-schedules-model.types'
import { VesselPositionStoredEntry } from '../src/models/vessel-position-model-impl'
import { TerminalInfo, convertTimeToLocal, PortMemoListType } from '../src/models/types/terminals-model.types';
import { BprAPIResponse, BprRecord } from '../src/models/types/voyage-best-practice-routes-impl.types';
import { CharacteristicsWithIMO } from '../src/services/vessel-plans-service';

export interface ZoneStoreEntry {
    zoneType: string;
    zonesData: string; // JSON stringified ZoneFeature[]
    lastUpdated: string;
}

export class DBManager extends Dexie {
    _plans!: Dexie.Table<VoyagePlan, string>
    _schedules!: Dexie.Table<ScheduleEntry, string>
    _vesselStatus: Dexie.Table<VesselStatusSchedule, string>
    _vesselStatusActual: Dexie.Table<VesselStatusActual, string>
    _position!: Dexie.Table<VesselPositionStoredEntry, string>
    _notifications!: Dexie.Table<any, string>
    _terminals!: Dexie.Table<TerminalInfo, string>
    _vesselCharacteristics!: Dexie.Table<VesselCharacteristicsIdb, string>
    _terminalsBatchTimeToLocal!: Dexie.Table<convertTimeToLocal, string>
    _batchTimeToLocal!: Dexie.Table<convertTimeToLocal, string>
    _bprs!: Dexie.Table<BprRecord, string>
    _oors!: Dexie.Table<BprRecord, string>
    _portMemos!: Dexie.Table<{}, string>
    _zones!: Dexie.Table<ZoneStoreEntry, string>

    constructor() {
        super('VesselDB')
        const vesselDbVersion = (<any>window)["appenv"].indexedDBConfig?.vesselDbVersion ?? 5;
       this.version(2).stores({
            plans: '++id',
            schedules: '++id',
            vesselStatus:'id',
            position: 'id',
            notifications: 'id',
            terminals: '++id',
        })

        this.version(3).stores({
            plans: null,
            schedules: null,
            terminals: null,
        })

        this.version(5).stores(this.mapNewTables());

        this.version(6).stores({
            zones: 'zoneType'
        });

        this.version(7).stores({
            oors: 'scheduleId'
        });

        this._plans = this.table('plans')
        this._schedules = this.table('schedules')
        this._vesselStatus = this.table('vesselStatus')
        this._vesselStatusActual = this.table('vesselStatusActual')
        this._position = this.table('position')
        this._terminals = this.table('terminals')
        this._notifications = this.table('notifications')
        this._vesselCharacteristics = this.table('vesselCharacteristics')
        this._batchTimeToLocal = this.table('batchTimeToLocal')
        this._bprs = this.table('bprs')
        this._oors = this.table('oors')
        this._portMemos = this.table('portMemos');
        this._zones = this.table('zones');
    }

    mapNewTables(){
        return{
            plans: 'scheduleId',
            schedules: 'id',
            vesselStatus:'id',
            vesselStatusActual:'id',
            position: 'id',
            notifications: 'id',
            terminals: 'locationId',
            vesselCharacteristics:'id',
            batchTimeToLocal:'inputTime',
            bprs: 'scheduleId',
            portMemos: 'terminalId',
        }
    }
    
    plan = {
        addPlan: async (plan: VoyagePlan) => {
            return await this._plans.put(plan)
        },
        addAll: async (plan: VoyagePlan[]) => {
            await this._plans.clear();
            return await this._plans.bulkPut(plan);
        },
        getPlan: async (id: number) => {
            const allPlans = await this._plans.toArray();
            const selected = allPlans.filter(e => id === e.id);
            return await selected[0];
        },
        getPlanByScheduleIds: async (scheduleIds: string[]) => {
            const allPlans = await this._plans.toArray();
            return allPlans.filter(e => scheduleIds.includes(e.scheduleId));
        },
        updatePlan: async (id: number, plan: VoyagePlan) => {
            const allPlans = await this._plans.toArray();
            const selected = allPlans.filter(e => id === e.id);
            return await this._plans.update(selected[0].scheduleId, plan)
        },
        updatePlanByScheduleId: async (scheduleId: string, plan: VoyagePlan) => {
            return await this._plans.update(scheduleId, plan)
        },
        getAllPlans: async () => {
            return await this._plans.toArray()
        },
        deletePlan: async (scheduleId: string) => {
            return await this._plans.delete(scheduleId)
        }, 
    }

    schedule = {
        addSchedule: async (schedule: ScheduleEntry) => {
            return await this._schedules.put(schedule)
        },
        getSchedule: async (id: string) => {
            return await this._schedules.get(id)
        },
        updateSchedule: async (id: string, schedule: ScheduleEntry) => {
            return await this._schedules.update(id, schedule)
        },
        getAllSchedules: async () => {
            const allSchedules = await this._schedules.toArray()
            allSchedules.sort((a, b) => a.etd - b.etd);
            return allSchedules;
        },
        deleteSchedule: async (id: string) => {
            return await this._schedules.delete(id)
        },
    }

    vesselStatus = {
        addVesselStatus: async (schedule: VesselStatusSchedule) => {
            const id = 'id' // only one status entry
            await this._vesselStatus.delete(id)
            return await this._vesselStatus.put({...schedule, id })
        },
        getVesselStatus: async () => {
            return await this._vesselStatus.toArray()
        },
        deleteVesselStatus: async (id: string) => {
            return await this._vesselStatus.delete(id)
        },
    }

    vesselStatusActual = {
        addVesselStatusActual: async (schedule: VesselStatusActual) => {
            const id = 'id' // only one status actual entry
            await this._vesselStatusActual.delete(id)
            return await this._vesselStatusActual.put({...schedule, id })
        },
        getVesselStatusActual: async () => {
            return await this._vesselStatusActual.toArray()
        },
        deleteVesselStatusActual: async (id: string) => {
            return await this._vesselStatusActual.delete(id)
        },
    }

    position = {
        addPosition: async (position: VesselPositionStoredEntry) => {
            const id = 'position-updated' // only one position entry
            await this._position.delete(id)
            return await this._position.put({ ...position, id })
        },
        updatePosition: async (position: VesselPositionStoredEntry) => {
            return await this._position.update('position-updated', position)
        },
        getPosition: async () => {
            return await this._position.get('position-updated') // Always fetch the same entry
        },
        deletePosition: async () => {
            return await this._position.delete('position-updated') // Remove the entry if needed
        },
    }

    notification = {
        addNotification: async (notification: any) => {
            return await this._notifications.put(notification)
        },

        getNotification: async (id: string | number) => {
            return await this._notifications.get(id.toString())
        },

        updateNotification: async(
            id: string | number, 
            notification: any
        ) => {
            return await this._notifications.update(id.toString(), notification)
        },

        getAllNotifications: async () => {
            return await this._notifications.toArray()
        },

        deleteNotification: async (notificationId: string | number) => {
            return await this._notifications.delete(notificationId.toString())
        },
        
        deleteAllNotifications: async () => {
            return await this._notifications.clear()
        }
    }

    terminal = {
        getAllTerminals: async () => {
            return await this._terminals.toArray()
        },
        addTerminal: async (terminal: TerminalInfo) => {
            return await this._terminals.put(terminal)
        },
        getTerminal: async (locationId: string) => {
            return await this._terminals.get(locationId)
        },
    }

    batchTimeToLocal = {
        getConvertedTime: async (inputTime: string) => {
            const timeObj = await this._batchTimeToLocal.get(inputTime);
            const convertedTime = timeObj?.convertedTime;
            return await convertedTime;
        },
        getTerminalTimeToLocal: async () => {
            return await this._batchTimeToLocal.toArray();
        },
        addTerminalTimeToLocal: async (timeObjArray:[]) => {
            return await this._batchTimeToLocal.bulkPut(timeObjArray);
        }
    }

    vesselCharacteristics = {
        getVesselCharacteristics: async () => {
            return await this._vesselCharacteristics.get('vessel-characteristics');
        },
        addVesselCharacteristics: async (vesselCharacteristics: CharacteristicsWithIMO) => {
            const id = 'vessel-characteristics' // only one position entry
            return await this._vesselCharacteristics.put({...vesselCharacteristics, id});
        }
    }

    bprs = {
        addBprs: async (bpr: BprRecord) => {
            return await this._bprs.put(bpr);
        },
        updateBprs: async (id: string, bpr: BprAPIResponse) => {
            return await this._bprs.update(id, bpr);
        },
        getScheduleBprs: async (id: string) => {
            return await this._bprs.get(id.toString());
        },
        getAllBprs: async () => {
            return await this._bprs.toArray()
        },
        deleteBpr: async (id: string) => {
            return await this._bprs.delete(id)
        },
        deleteAll: async () => {
            return await this._bprs.clear();
        }
    }

    oors = {
        addOors: async (oor: BprRecord) => {
            return await this._oors.put(oor);
        },
        getScheduleOors: async (id: string) => {
            return await this._oors.get(id.toString());
        },
        getAllOors: async () => {
            return await this._oors.toArray();
        },
        deleteOor: async (id: string) => {
            return await this._oors.delete(id);
        },
        deleteAll: async () => {
            return await this._oors.clear();
        }
    }

    portMemos = {
        getPortMemo: async (terminalId: string) => {
            return await (await this._portMemos.get(terminalId)) || {};
        },
        getAllPortMemos: async () => {
            return await this._portMemos.toArray();
        },
        addAllPortMemos: async (portMemosArray: PortMemoListType[]) => {
            this._portMemos.clear();
            return await this._portMemos.bulkPut(portMemosArray);
        }   
    }

    zones = {
        getZonesByType: async (zoneType: string): Promise<ZoneStoreEntry | undefined> => {
            return await this._zones.get(zoneType);
        },
        getAllZones: async (): Promise<ZoneStoreEntry[]> => {
            return await this._zones.toArray();
        },
        addZones: async (entry: ZoneStoreEntry) => {
            return await this._zones.put(entry);
        },
        deleteZonesByType: async (zoneType: string) => {
            return await this._zones.delete(zoneType);
        },
        deleteAllZones: async () => {
            return await this._zones.clear();
        }
    }
}

const vesselDbInstance = new DBManager();

export const getDatabase = (): DBManager => vesselDbInstance;
